import { useEffect, useMemo, useState } from "react";

import type { CartItemDraft } from "../types/cart";
import type { Product, StockProductDescription } from "../types/marketplace";
import { formatCurrency, formatNumber } from "../utils/formatters";

interface ProductDetailProps {
  onAddToCart?: (item: CartItemDraft) => void;
  product: Product;
  onClose: () => void;
}

const colorCategoryId = 1;

function uniqueByDescription(items: StockProductDescription[]): StockProductDescription[] {
  const result: StockProductDescription[] = [];
  const seen = new Set<number>();

  items.forEach((item) => {
    if (!seen.has(item.stockDescriptionId)) {
      seen.add(item.stockDescriptionId);
      result.push(item);
    }
  });

  return result;
}

function variantGroupLabel(options: StockProductDescription[], hasSelectedColor: boolean): string {
  if (!hasSelectedColor) {
    return "Opciones disponibles";
  }

  const labels = Array.from(
    new Set(
      options
        .map((variant) => variant.stockDescription.stockCategoryName)
        .filter((label): label is string => Boolean(label))
    )
  );

  return labels.length === 1 ? labels[0] : "Talle / variante";
}

function variantLabel(variant: StockProductDescription): string {
  const category = variant.stockDescription.stockCategoryName;
  const name = variant.stockDescription.name ?? "Opción";

  return category ? `${category}: ${name}` : name;
}

function ProductDetail({ onAddToCart, product, onClose }: ProductDetailProps) {
  const variants = product.stockProductDescriptions ?? [];
  const colorVariants = useMemo(
    () =>
      uniqueByDescription(
        variants.filter((variant) => variant.stockDescription.stockCategoryId === colorCategoryId)
      ),
    [variants]
  );
  const standaloneVariants = useMemo(
    () =>
      uniqueByDescription(
        variants.filter((variant) => variant.stockDescription.stockCategoryId !== colorCategoryId)
      ),
    [variants]
  );

  const [selectedColorId, setSelectedColorId] = useState<number | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [addedMessage, setAddedMessage] = useState<string>("");

  useEffect(() => {
    setSelectedColorId(colorVariants[0]?.stockDescriptionId ?? null);
    setSelectedVariantId(null);
    setQuantity(1);
    setAddedMessage("");
  }, [product.id, colorVariants]);

  const selectedColor = colorVariants.find(
    (variant) => variant.stockDescriptionId === selectedColorId
  );

  const sizeOptions = useMemo(() => {
    if (!selectedColor) {
      return standaloneVariants;
    }

    const sameCombination = variants.filter(
      (variant) =>
        variant.combination === selectedColor.combination &&
        variant.stockDescription.stockCategoryId !== colorCategoryId
    );

    return sameCombination.length > 0 ? sameCombination : [selectedColor];
  }, [selectedColor, standaloneVariants, variants]);

  const selectedVariant =
    sizeOptions.find((variant) => variant.id === selectedVariantId) ??
    (sizeOptions.length === 1 ? sizeOptions[0] : undefined);

  const stockLimit = selectedVariant?.stock ?? product.stock ?? 0;
  const selectedImage = selectedVariant?.image || selectedColor?.image || product.image;
  const optionLabel = variantGroupLabel(sizeOptions, Boolean(selectedColor));
  const canBuy = stockLimit > 0 && (variants.length === 0 || Boolean(selectedVariant));
  const selectedVariantLabels = useMemo(() => {
    const labels: string[] = [];

    if (selectedColor) {
      labels.push(variantLabel(selectedColor));
    }

    if (selectedVariant && selectedVariant.id !== selectedColor?.id) {
      labels.push(variantLabel(selectedVariant));
    }

    return labels;
  }, [selectedColor, selectedVariant]);
  const selectedVariantLabel =
    selectedVariantLabels.length > 0 ? selectedVariantLabels.join(" / ") : null;
  const selectedCartVariantId = selectedVariant?.id ?? selectedColor?.id ?? null;

  const handleQuantityChange = (nextQuantity: number) => {
    const normalizedQuantity = Math.max(1, Math.min(nextQuantity, Math.max(stockLimit, 1)));
    setQuantity(normalizedQuantity);
  };

  const handleAdd = () => {
    const formattedQuantity = formatNumber(quantity);
    const summary = selectedVariantLabel
      ? `${formattedQuantity} x ${selectedVariantLabel}`
      : `${formattedQuantity} unidad(es)`;

    onAddToCart?.({
      product,
      quantity,
      stockLimit,
      unitPrice: product.price,
      variantId: selectedCartVariantId,
      variantImage: selectedImage,
      variantLabel: selectedVariantLabel
    });
    setAddedMessage(`Agregado al carrito: ${summary}`);
  };

  return (
    <div className="product-detail-overlay" role="dialog" aria-modal="true">
      <article className="product-detail">
        <button className="product-detail-close" onClick={onClose} type="button">
          x
        </button>

        <div className="product-detail-media">
          <img src={selectedImage} alt={product.name} decoding="async" />
        </div>

        <div className="product-detail-info">
          <small className="product-detail-store">{product.clientName ?? "Tienda"}</small>
          <h2>{product.name}</h2>

          <div className="price-row">
            <strong>{formatCurrency(product.price)}</strong>
            {product.oldPrice && <del>{formatCurrency(product.oldPrice)}</del>}
          </div>

          {product.discount && <span className="detail-discount">{product.discount}</span>}

          {product.description && <p className="product-detail-description">{product.description}</p>}

          {colorVariants.length > 0 && (
            <section className="variant-block">
              <h3>Color</h3>
              <div className="variant-options">
                {colorVariants.map((variant) => (
                  <button
                    className={selectedColorId === variant.stockDescriptionId ? "selected" : ""}
                    key={variant.id}
                    onClick={() => {
                      setSelectedColorId(variant.stockDescriptionId);
                      setSelectedVariantId(null);
                      setQuantity(1);
                      setAddedMessage("");
                    }}
                    type="button"
                  >
                    {variant.stockDescription.rgb && (
                      <span
                        className="color-dot"
                        style={{ backgroundColor: variant.stockDescription.rgb }}
                      />
                    )}
                    {variant.stockDescription.name ?? "Color"}
                  </button>
                ))}
              </div>
            </section>
          )}

          {sizeOptions.length > 0 && (
            <section className="variant-block">
              <h3>{optionLabel}</h3>
              <div className="variant-options">
                {sizeOptions.map((variant) => (
                  <button
                    className={selectedVariant?.id === variant.id ? "selected" : ""}
                    disabled={variant.stock <= 0}
                    key={variant.id}
                    onClick={() => {
                      setSelectedVariantId(variant.id);
                      setQuantity(1);
                      setAddedMessage("");
                    }}
                    type="button"
                  >
                    {variant.stockDescription.name ?? "Opción"}
                    <small>{formatNumber(variant.stock)} disp.</small>
                  </button>
                ))}
              </div>
            </section>
          )}

          {stockLimit <= 0 ? (
            <p className="plain-stock empty">Sin stock</p>
          ) : (
            variants.length === 0 && (
              <p className="plain-stock">Stock disponible: {formatNumber(product.stock ?? 0)}</p>
            )
          )}

          <div className="quantity-box">
            <button onClick={() => handleQuantityChange(quantity - 1)} type="button">
              -
            </button>
            <span>{formatNumber(quantity)}</span>
            <button onClick={() => handleQuantityChange(quantity + 1)} type="button">
              +
            </button>
          </div>

          <button
            className="detail-add-button"
            disabled={!canBuy}
            onClick={handleAdd}
            type="button"
          >
            {canBuy ? "Agregar al carrito" : "Sin stock"}
          </button>

          {addedMessage && <p className="detail-added-message">{addedMessage}</p>}
        </div>
      </article>
    </div>
  );
}

export default ProductDetail;
